// Terminal (kiosk) mode — `page.chrome.terminal` — for a screen that stays on a
// wall or a counter all day: fullscreen, the screen kept awake, and sound
// allowed for arrival chimes. Browsers grant fullscreen and audio only from a
// user gesture, so the page shows a one-tap Start overlay that asks for them;
// the wake lock is re-taken whenever the tab becomes visible again (browsers
// drop it on every hide). Every capability is best-effort: an unsupported one
// is skipped, never an error.
//
// Mounted by each LIVE surface (Run view / preview, downloaded runtime). The
// static site and the designer canvas never mount it.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { PageTerminal } from '@twasta/page-schema'
import { armAudioOnFirstGesture, unlockAudio } from './chime'
import { tw } from '../testHooks'

type WakeLockSentinelLike = { release: () => Promise<void>; released?: boolean }

async function requestWakeLock(): Promise<WakeLockSentinelLike | null> {
  try {
    const wl = (navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<WakeLockSentinelLike> } }).wakeLock
    return wl ? await wl.request('screen') : null
  } catch {
    return null   // denied (battery saver, not visible) — try again on the next show
  }
}

async function enterFullscreen(): Promise<void> {
  try {
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }
    if (document.fullscreenElement) return
    if (el.requestFullscreen) await el.requestFullscreen()
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen()
  } catch { /* refused: the page still works windowed */ }
}

export function TerminalMode({ config }: { config: PageTerminal | undefined }): React.ReactElement | null {
  const wants = !!config && (config.fullscreen || config.wakeLock || config.sound)
  const [started, setStarted] = useState(false)
  const [isFull, setIsFull] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement)
  const lock = useRef<WakeLockSentinelLike | null>(null)

  useEffect(() => { if (wants && config?.sound) armAudioOnFirstGesture() }, [wants, config?.sound])

  // Hold the wake lock while started; re-take it after every hide/show.
  useEffect(() => {
    if (!started || !config?.wakeLock) return undefined
    let alive = true
    const take = async () => {
      if (document.visibilityState !== 'visible') return
      if (lock.current && !lock.current.released) return
      const s = await requestWakeLock()
      if (alive) lock.current = s
      else void s?.release().catch(() => {})
    }
    void take()
    const onVis = () => { void take() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      alive = false
      document.removeEventListener('visibilitychange', onVis)
      void lock.current?.release().catch(() => {})
      lock.current = null
    }
  }, [started, config?.wakeLock])

  useEffect(() => {
    if (!wants) return undefined
    const onFs = () => setIsFull(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [wants])

  const start = useCallback(() => {
    if (config?.sound) unlockAudio()
    if (config?.fullscreen) void enterFullscreen()
    setStarted(true)
  }, [config?.sound, config?.fullscreen])

  if (!wants) return null

  if (!started) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Start terminal"
        style={{
          position: 'fixed', inset: 0, zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'color-mix(in srgb, var(--pg-bg, #fff) 70%, transparent)', backdropFilter: 'blur(4px)',
        }}
      >
        <button
          type="button"
          autoFocus
          onClick={start}
          {...tw('action', 'terminal-start')}
          style={{
            font: 'inherit', fontSize: 22, fontWeight: 600, padding: '18px 36px', minWidth: 220, minHeight: 64,
            borderRadius: 'var(--pg-radius, 12px)', border: 'none', cursor: 'pointer',
            background: 'var(--pg-primary, #2563eb)', color: 'var(--pg-on-primary, #fff)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
          }}
        >
          {config?.startLabel || 'Start'}
        </button>
      </div>
    )
  }

  // Started but no longer fullscreen (Esc, a system dialog): offer it back.
  if (config?.fullscreen && !isFull) {
    return (
      <button
        type="button"
        onClick={() => { void enterFullscreen() }}
        aria-label="Enter full screen"
        title="Enter full screen"
        {...tw('action', 'terminal-fullscreen')}
        style={{
          position: 'fixed', right: 12, bottom: 12, zIndex: 11000, width: 48, height: 48, borderRadius: 999,
          border: 'none', cursor: 'pointer', fontSize: 20,
          background: 'var(--pg-primary, #2563eb)', color: 'var(--pg-on-primary, #fff)', opacity: 0.85,
        }}
      >
        ⤢
      </button>
    )
  }
  return null
}
